import { client } from "@lib/api"
import { getPlatform, openLink } from "@lib/telegram"
import { decodeHtmlEntities } from "@lib/utils"
import { NavigateFunction } from "react-router"
import useSWR from "swr"

type RawAnnouncement = {
  uuid: string
  announcement_text: string
  primary_color: string
  secondary_color: string
  button_text: string
  button_destination: string | null
  redirection_type: 'INTERNAL' | 'EXTERNAL' | null
  platform: 'TMA' | 'TMA_IOS' | 'TMA_ANDROID' | 'MOBILE' | 'MOBILE_IOS' | 'MOBILE_ANDROID' | 'ALL'
  is_active: boolean
  priority: number
  created_at: string
  updated_at: string
}

type Response = {
  data: RawAnnouncement[]
}

export type Announcement = {
  uuid: string
  announcement_text: string
  primary_color: string
  secondary_color: string
  button_text: string
  action: (() => Promise<void> | void) | undefined
}

const useAnnouncement = (navigate: NavigateFunction) => {
  const { data, isLoading, error } = useSWR('/wallet/announcements', client.doUnauthenticatedGet)

  let announcements: Announcement[] = []

  // Get current platform from Telegram WebApp
  const currentPlatform = getPlatform()

  announcements = (data as Response)?.data
    ?.filter((item) => {
      // Show announcements that match:
      // 1. General TMA platform
      // 2. ALL platforms
      // 3. OS-specific platforms (TMA_IOS or TMA_ANDROID)
      if (item.platform === 'ALL' || item.platform === 'TMA') {
        return true
      }

      // Check OS-specific platforms
      if (currentPlatform === 'android' && item.platform === 'TMA_ANDROID') {
        return true
      }

      if (currentPlatform !== 'android' && item.platform === 'TMA_IOS') {
        return true
      }

      return false
    })
    ?.map((item) => {
      let action = undefined
      if (item.redirection_type === 'INTERNAL' && item.button_destination && item.button_text) {
        action = async () => await navigate(item.button_destination as string)
      }
      if (item.redirection_type === 'EXTERNAL' && item.button_destination && item.button_text) {
        action = async () => openLink(item.button_destination as string)
      }

      return {
        uuid: item.uuid,
        announcement_text: decodeHtmlEntities(item.announcement_text),
        primary_color: item.primary_color,
        secondary_color: item.secondary_color,
        button_text: decodeHtmlEntities(item.button_text),
        action
      }
    }) || []


  return {
    data: announcements,
    isLoading,
    error
  }
}

export default useAnnouncement